import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface PixelTextProps {
  children: ReactNode;
  className?: string;
}

/**
 * PixelText - Simula lo stile voxel/pixel dell'icona Michelangelo
 * con ombra 3D isometrica e bordi netti
 */
export function PixelText({ children, className }: PixelTextProps) {
  return (
    <span
      className={cn(
        'relative inline-block font-bold tracking-tight',
        'bg-gradient-to-r from-[#6b8cff] via-[#8b5cf6] to-[#a855f7]',
        'bg-clip-text text-transparent',
        className,
      )}
      style={{
        fontFamily: "'Pixelify Sans', 'Inter', sans-serif",
        // Ombra 3D voxel-style che simula l'icona
        textShadow: `
          1px 1px 0 rgba(107, 140, 255, 0.4),
          2px 2px 0 rgba(107, 140, 255, 0.3),
          3px 3px 0 rgba(107, 140, 255, 0.2),
          4px 4px 0 rgba(107, 140, 255, 0.1),
          5px 5px 0 rgba(107, 140, 255, 0.05),
          6px 6px 12px rgba(107, 140, 255, 0.3)
        `,
        WebkitTextStroke: '1px rgba(107, 140, 255, 0.3)',
        transform: 'translateZ(0)',
        willChange: 'transform',
      }}
    >
      {children}
    </span>
  );
}

/**
 * PixelTextSolid - Versione fedele all'icona Michelangelo
 * Bianco puro con ombre grigio scuro (stile voxel icona app)
 */
export function PixelTextSolid({ children, className }: PixelTextProps) {
  return (
    <span
      className={cn('relative inline-block font-bold tracking-tight', className)}
      style={{
        fontFamily: "'Pixelify Sans', 'Inter', sans-serif",
        color: '#ffffff',
        // Ombra 3D stile voxel grigio scuro (come l'icona)
        textShadow: `
          1px 1px 0 #333333,
          2px 2px 0 #2a2a2a,
          3px 3px 0 #222222,
          4px 4px 0 #1a1a1a,
          5px 5px 0 #111111,
          6px 6px 0 #0a0a0a,
          7px 7px 0 #050505,
          8px 8px 16px rgba(0, 0, 0, 0.8)
        `,
        transform: 'translateZ(0) translate(-3px, -3px)',
        willChange: 'transform',
      }}
    >
      {children}
    </span>
  );
}

/**
 * PixelTextOutline - Versione outline con effetto pixel
 * simile agli strumenti bianchi dell'icona
 */
export function PixelTextOutline({ children, className }: PixelTextProps) {
  return (
    <span
      className={cn('relative inline-block font-bold tracking-tight', className)}
      style={{
        fontFamily: "'Inter', 'SF Mono', 'Monaco', monospace",
        color: 'transparent',
        WebkitTextStroke: '2px #ffffff',
        // Ombra 3D grigia che simula l'effetto voxel
        textShadow: `
          2px 2px 0 rgba(0, 0, 0, 0.8),
          4px 4px 0 rgba(0, 0, 0, 0.6),
          6px 6px 0 rgba(0, 0, 0, 0.4),
          8px 8px 0 rgba(0, 0, 0, 0.2),
          10px 10px 20px rgba(0, 0, 0, 0.5)
        `,
        transform: 'translateZ(0)',
        willChange: 'transform',
      }}
    >
      {children}
    </span>
  );
}
